import { KYB_BASE_FIELDS } from "./base/kyb.base";
import { CO_KYB_OVERRIDE } from "./overrides/CO.kyb";
import { DE_KYB_OVERRIDE } from "./overrides/DE.kyb";
import { SOLE_PROP_KYB_OVERRIDE } from "./overrides/sole-proprietorship.kyb";
import { US_KYB_OVERRIDE } from "./overrides/US.kyb";
import type {
	FieldConfig,
	FieldConfigOverride,
	FieldResolverContext,
	ResolvedFieldConfig,
} from "./types";

/** All registered overrides — add new country files here */
const ALL_OVERRIDES: FieldConfigOverride[] = [
	SOLE_PROP_KYB_OVERRIDE,
	US_KYB_OVERRIDE,
	CO_KYB_OVERRIDE,
	DE_KYB_OVERRIDE,
];

/** Default thresholds (no override applied) */
const DEFAULT_THRESHOLDS = {
	uboTrigger: 25,
	minOwnershipPercentage: 10,
};

/**
 * Checks whether an override matches the given resolver context.
 * All specified keys on the override must match; omitted keys match any value.
 */
function matchesContext(
	override: FieldConfigOverride,
	ctx: FieldResolverContext,
): boolean {
	// Flow type must match (or override covers BOTH)
	if (override.flowType !== "BOTH" && override.flowType !== ctx.flowType)
		return false;

	// Country constraint — if specified, must match
	if (
		override.incorporationCountry &&
		override.incorporationCountry !== ctx.incorporationCountry
	)
		return false;

	// Entity type constraint — if specified, must match
	if (override.entityType && override.entityType !== ctx.entityType)
		return false;

	// Date-based effectivity
	const today = new Date().toISOString().slice(0, 10);
	if (override.effectiveFrom && override.effectiveFrom > today) return false;
	if (override.effectiveUntil && override.effectiveUntil < today) return false;

	return true;
}

/**
 * Merge a partial FieldConfig override into the existing config entry.
 * Returns a new FieldConfig (does not mutate the original).
 */
function mergeFieldOverride(
	base: FieldConfig,
	partial: Partial<Omit<FieldConfig, "fieldId">>,
): FieldConfig {
	return {
		...base,
		...partial,
		// Deep-merge validationRules if provided (append, don't replace)
		validationRules: partial.validationRules
			? [...base.validationRules, ...partial.validationRules]
			: base.validationRules,
		// Deep-merge label/helpText/placeholder (override wins per locale key)
		label: partial.label ? { ...base.label, ...partial.label } : base.label,
		helpText: partial.helpText
			? { ...(base.helpText ?? {}), ...partial.helpText }
			: base.helpText,
		placeholder: partial.placeholder
			? { ...(base.placeholder ?? {}), ...partial.placeholder }
			: base.placeholder,
	};
}

/**
 * Resolve the complete field config for the given context.
 *
 * Resolution algorithm:
 * 1. Start with base config (all fields, default visible/required)
 * 2. Collect matching overrides sorted by priority ASC
 * 3. Apply each override: fieldOverrides, additionalFields, removedFields
 * 4. Apply threshold overrides
 * 5. Sort by (step ASC, order ASC)
 * 6. Return visible=true fields
 */
export function resolveFieldConfig(
	ctx: FieldResolverContext,
): ResolvedFieldConfig {
	// Deep-clone base fields to avoid mutation
	const fields: FieldConfig[] = KYB_BASE_FIELDS.map((f) => ({ ...f }));

	const thresholds = { ...DEFAULT_THRESHOLDS };

	// Collect matching overrides sorted by priority ASC (lower first, higher wins)
	const matchingOverrides = ALL_OVERRIDES.filter((o) =>
		matchesContext(o, ctx),
	).sort((a, b) => a.priority - b.priority);

	for (const override of matchingOverrides) {
		// Apply field property overrides
		if (override.fieldOverrides) {
			for (const [fieldId, partial] of Object.entries(
				override.fieldOverrides,
			)) {
				const idx = fields.findIndex((f) => f.fieldId === fieldId);
				if (idx !== -1) {
					fields[idx] = mergeFieldOverride(fields[idx]!, partial);
				}
			}
		}

		// Inject additional fields
		if (override.additionalFields) {
			for (const additionalField of override.additionalFields) {
				// Only add if not already present
				if (!fields.some((f) => f.fieldId === additionalField.fieldId)) {
					fields.push({ ...additionalField });
				}
			}
		}

		// Remove fields (set visible=false)
		if (override.removedFields) {
			for (const fieldId of override.removedFields) {
				const idx = fields.findIndex((f) => f.fieldId === fieldId);
				if (idx !== -1) {
					fields[idx] = { ...fields[idx]!, visible: false };
				}
			}
		}

		// Apply threshold overrides
		if (override.uboDisclosureThreshold !== undefined) {
			thresholds.uboTrigger = override.uboDisclosureThreshold;
		}
		if (override.ownershipDisclosureThreshold !== undefined) {
			thresholds.minOwnershipPercentage = override.ownershipDisclosureThreshold;
		}
	}

	// Filter to visible fields and sort by (step ASC, order ASC)
	const visibleFields = fields
		.filter((f) => f.visible)
		.sort((a, b) => (a.step !== b.step ? a.step - b.step : a.order - b.order));

	const fieldMap: Record<string, FieldConfig> = {};
	for (const field of visibleFields) {
		fieldMap[field.fieldId] = field;
	}

	return { fields: visibleFields, fieldMap, thresholds };
}
